/* eslint-disable max-lines-per-function */
import { ReactComponent as ChainIcon } from '@vkontakte/icons/src/svg/16/chain_outline_16.svg';
import config from 'Cloud/config';
import React, { type ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { IS_B2B_BIZ_USER, IS_FREE_BIZ_SAAS_USER, IS_PAID_B2B_TARIFF } from 'reactApp/appHelpers/configHelpers';
import { showCommentFileOption } from 'reactApp/appHelpers/featuresHelpers/features/showCommentFileOption';
import { SharingNewDropdown } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown';
import {
    getAccessFileRightsBizOptions,
    getAccessFolderRightsOptionsBiz,
    getBizSaasAccessRightsOptions,
    QA_VALUE,
    TOOLTIP_OFFSET,
} from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { sendGa } from 'reactApp/components/SharingNewBiz/SharingNew.helpers';
import { AccessStatus } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import styles from 'reactApp/components/SharingNewBiz/Weblink/SharingNewWeblink.css';
import { getAvailableConfiguredEditorsFor, getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import type { XrayItemType } from 'reactApp/modules/home/home.types';
import { weblinkHandleAccessRightChangeSaga } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { type isMountedOrSharedFolder, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import { AccessRights } from 'reactApp/types/Tree';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

const getAccessRights = (
    isReadOnly: boolean,
    isPublicReadWrite: boolean,
    isDownloadable: boolean,
    isFreeBizUser: boolean,
    isCommentable: boolean
): AccessStatus => {
    if (showCommentFileOption && isCommentable) {
        return AccessStatus.Comment;
    }

    if (isFreeBizUser && isPublicReadWrite) {
        return AccessStatus.ReadOnly;
    }

    if (isDownloadable) {
        return AccessStatus.Download;
    }

    if (isPublicReadWrite && !isReadOnly) {
        return AccessStatus.Edit;
    }

    return AccessStatus.ReadOnly;
};

interface Props {
    item?: PublishItem;
    fromEditor?: boolean;
    isSharedOrMouted: ReturnType<typeof isMountedOrSharedFolder>;
    xrayItemType: XrayItemType;
}

export const AccessRightsDropdown: React.FC<Props> = ({ item, fromEditor, isSharedOrMouted, xrayItemType }) => {
    const dispatch = useDispatch();
    const isPhone = EnvironmentSelectors.isPhone();
    const isFolder = item?.isFolder;
    const isDownloadable = Boolean(item?.weblinkDownloadable);
    const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
    const isDocumentForEdit = item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER ? true : isFileForCoEditing;
    const isPublic = Boolean(item?.weblink);
    const currentStorage = useSelector(getCurrentStorage);
    const storage = fromEditor ? EStorageType.home : currentStorage;
    const parent = useSelector((state: RootState) => getStorageItemById(state, storage, item?.parent));
    const isReadOnlyItem = item?.isReadOnly;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isReadOnlyParent = parent?.isReadOnly;
    const isReadOnly = !isReadOnlyItem && isSharedOrMouted.isMounted ? isReadOnlyParent : isReadOnlyItem;

    const folderOptions = getAccessFolderRightsOptionsBiz();
    const fileOptions = IS_B2B_BIZ_USER && !IS_PAID_B2B_TARIFF ? getBizSaasAccessRightsOptions() : getAccessFileRightsBizOptions();

    const options = isFolder ? folderOptions : fileOptions;
    const { isDomainAndEditRestricted, isDomainAndDownloadRestricted } = getDomainRestrictionsForItems(item);
    const isRO = isFolder && isReadOnly;
    const isPublicReadWrite = item?.weblinkAccessRights === AccessRights.write;
    const isCommentable = item?.weblinkAccessRights === AccessRights.comment;

    const accessRights = getAccessRights(isReadOnly, isPublicReadWrite, isDownloadable, IS_FREE_BIZ_SAAS_USER, isCommentable);

    const isEditable = useSelector((state: RootState) => getEditorsFor(state, item))?.length > 0;
    const isItemHaveConfiguredEditors = useSelector((state: RootState) => item && getAvailableConfiguredEditorsFor(state, item).length);

    const mapId2QaValue = useCallback(
        (id) => {
            let qaValue = id;
            if (isFolder && id === AccessStatus.Download) {
                qaValue = AccessStatus.Upload;
            }
            return qaValue;
        },
        [isFolder]
    );

    const renderAccessRightsDropdown = useCallback(
        (onClick: void): ReactElement => {
            const list = Object.values(options).map((item) => {
                const qaValue = mapId2QaValue(item.id);
                return {
                    id: item.id,
                    text: item.text,
                    check: item.id === accessRights,
                    qaValue,
                };
            });

            const needOmitEdit =
                ((!isDocumentForEdit || !isItemHaveConfiguredEditors) && !isFolder) || isReadOnlyParent || isDomainAndEditRestricted;
            if (needOmitEdit) {
                const editItemIdx = list.findIndex((item) => item.id === AccessStatus.Edit);
                if (editItemIdx > -1) {
                    list.splice(editItemIdx, 1);
                }
            }

            const needOmitDownload = isDomainAndDownloadRestricted;
            if (needOmitDownload) {
                const downloadItemIdx = list.findIndex((item) => item.id === AccessStatus.Download);
                if (downloadItemIdx > -1) {
                    list.splice(downloadItemIdx, 1);
                }
            }

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [
            options,
            isDocumentForEdit,
            isItemHaveConfiguredEditors,
            isFolder,
            isDomainAndEditRestricted,
            isDomainAndDownloadRestricted,
            isReadOnlyParent,
            mapId2QaValue,
            accessRights,
        ]
    );

    const handleAccessRightsChange = useCallback(
        (newAccessRight: AccessStatus): void => {
            if (!isPublic || accessRights === newAccessRight) {
                return;
            }

            if (!item) {
                return;
            }

            sendGa('access-rights');
            sendPaymentGa({
                eventCategory: ECategoryGa.public,
                action: 'edit-type-access',
                type_access: newAccessRight,
                public_id: item?.weblink,
            });

            dispatch(
                weblinkHandleAccessRightChangeSaga({
                    item,
                    isEditable,
                    isReadOnlyProperty: isReadOnly,
                    newAccessRight,
                    storage,
                    xrayItemType,
                })
            );
        },
        [accessRights, dispatch, isEditable, isPublic, isReadOnly, item, storage, xrayItemType]
    );

    const renderAccessRights = useCallback((): ReactElement => {
        const item = options[accessRights];
        const qaValue = mapId2QaValue(item?.id);
        return (
            <div className={styles.rightsItem} data-qa-item={QA_VALUE.accessItem}>
                <div className={styles.titleWrapper}>
                    <span className={styles.icon}>
                        <ChainIcon />
                    </span>
                    <span className={styles.rightsTitle}>Права пользователей по ссылке</span>
                </div>
                <div className={styles.dropdown}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={qaValue}
                        as={SharingNewDropdown}
                        value={item?.text}
                        content={renderAccessRightsDropdown}
                        onClickItem={handleAccessRightsChange}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.accessDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        description="Права пользователей по ссылке"
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        noPageOffset={isPhone}
                    />
                </div>
            </div>
        );
    }, [accessRights, handleAccessRightsChange, isPhone, mapId2QaValue, options, renderAccessRightsDropdown]);

    if (isRO) {
        return null;
    }

    return renderAccessRights();
};
